import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { MutationResponse } from 'global';
import { useMutation, useQuery } from '@apollo/client';

import { quoteAPI } from '../api';
import { Quote, QuoteFormValues } from '../types';
import { createOrUpdateQuoteDTOSchema } from '../model/schema';
import { CreateOrUpdateQuoteVariables, GetQuoteResponse, GetQuoteVariables, QuoteAPINameSpase } from '../api/types';

export function useQuote(voId?: string) {
  const {
    error,
    data: quoteResponse,
    loading: isQuoteLoading,
  } = useQuery<GetQuoteResponse, GetQuoteVariables>(quoteAPI.getQuote, {
    skip: !voId,
    notifyOnNetworkStatusChange: true,
    variables: { vendorOpportunityId: voId! },
  });

  const [onUpdate, { loading }] = useMutation<QuoteAPINameSpase<MutationResponse<Quote>>, CreateOrUpdateQuoteVariables>(
    quoteAPI.upsertQuote
  );

  const handleCreateOrUpdateQuote = useCallback(
    async (voId: string, updateDTO: QuoteFormValues) => {
      try {
        const collectDTO: CreateOrUpdateQuoteVariables['input'] = { vendorOpportunityId: voId, ...updateDTO };

        const { success, error, data } = createOrUpdateQuoteDTOSchema.safeParse(collectDTO);

        if (success) {
          await onUpdate({ variables: { input: data } });
        } else {
          logError('handleUpdateQuote input parse failed', error);
        }
      } catch (error) {
        logError(error);
      }
    },
    [onUpdate]
  );

  return {
    error,
    quoteAPI,
    isQuoteLoading,
    handleCreateOrUpdateQuote,
    isQuoteCreatingOrUpdating: loading,
    quote: quoteResponse?.vendorOpportunity?.quote ?? null,
  };
}
